/**
 * Custom Variant Selector
 * Handles variant selection, quantity, pricing, and cart integration for single products
 */

(function() {
  'use strict';

  // Namespace for the variant selector
  window.CustomVariantSelector = window.CustomVariantSelector || {};

  /**
   * Custom Variant Selector Class
   */
  class VariantSelector {
    constructor(containerId) {
      this.container = document.getElementById(containerId);
      if (!this.container) {
        console.error('Custom Variant Selector: Container not found:', containerId);
        return;
      }

      // Load data from window object
      this.data = window.customVariantSelectorData && window.customVariantSelectorData[containerId];
      if (!this.data) {
        console.error('Custom Variant Selector: Data not found for:', containerId);
        return;
      }

      // State
      this.selectedVariant = null;
      this.quantity = 1;

      // DOM Elements
      this.variantButtons = this.container.querySelectorAll('.variant-btn');
      this.addToCartBtn = this.container.querySelector('.custom-variant-selector__add-to-cart');
      this.qtyInput = this.container.querySelector('.custom-variant-selector__qty-input');
      this.qtyDecreaseBtn = this.container.querySelector('.custom-variant-selector__qty-btn[data-action="decrease"]');
      this.qtyIncreaseBtn = this.container.querySelector('.custom-variant-selector__qty-btn[data-action="increase"]');
      this.addToCartText = this.container.querySelector('.custom-variant-selector__add-to-cart-text');
      this.addToCartPrice = this.container.querySelector('.custom-variant-selector__add-to-cart-price');
      this.validationMessage = this.container.querySelector('.custom-variant-selector__validation-message');
      this.errorMessage = this.container.querySelector('.custom-variant-selector__error-message');

      // Initialize
      this.init();
    }

    init() {
      // Set initial selected variant if one is already selected
      if (this.data.currentVariantId) {
        const variant = this.data.variants.find(v => v.id === this.data.currentVariantId);
        if (variant) {
          this.selectedVariant = variant;
        }
      }
      
      // If no variants (single variant product), auto-select the first variant
      if (!this.data.hasVariants && this.data.variants.length === 1) {
        this.selectedVariant = this.data.variants[0];
      }

      this.bindEvents();
      this.updatePriceDisplay();
      this.validateSelections();
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Variant button selection
      this.variantButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          if (!btn.disabled) {
            this.selectVariant(btn);
          }
        });
      });

      // Quantity controls
      if (this.qtyDecreaseBtn) {
        this.qtyDecreaseBtn.addEventListener('click', () => this.changeQuantity(-1));
      }
      if (this.qtyIncreaseBtn) {
        this.qtyIncreaseBtn.addEventListener('click', () => this.changeQuantity(1));
      }
      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', (e) => {
          const value = parseInt(e.target.value) || 1;
          this.quantity = Math.max(1, Math.min(99, value));
          this.qtyInput.value = this.quantity;
          this.updatePriceDisplay();
        });
      }

      // Add to cart
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
      }
    }

    /**
     * Select a variant
     */
    selectVariant(button) {
      const variantId = parseInt(button.dataset.variantId);
      const variant = this.data.variants.find(v => v.id === variantId);
      
      if (!variant) {
        console.error('Variant not found:', variantId);
        return;
      }

      // Update state
      this.selectedVariant = variant;

      // Update button UI
      this.variantButtons.forEach(btn => {
        btn.classList.remove('selected');
      });
      button.classList.add('selected');

      // Update product image in the product gallery if available
      if (variant.image) {
        this.updateProductImage(variant.image);
      }

      // Update URL with variant ID for shareable links
      this.updateURL(variantId);

      // Update price display
      this.updatePriceDisplay();

      // Validate selections
      this.validateSelections();
    }

    /**
     * Update product gallery image
     */
    updateProductImage(imageUrl) {
      // Try to update the main product image in various common structures
      const selectors = [
        '.product__media-wrapper img',
        '.product-media-container img',
        '.product__media img',
        '[data-product-featured-image]',
        '.product-single__media img'
      ];

      for (const selector of selectors) {
        const img = document.querySelector(selector);
        if (img) {
          img.src = imageUrl;
          img.srcset = '';
          break;
        }
      }
    }

    /**
     * Update URL with selected variant
     */
    updateURL(variantId) {
      if (!history.replaceState) return;
      
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      history.replaceState({}, '', url.toString());
    }

    /**
     * Change quantity
     */
    changeQuantity(delta) {
      const newQty = this.quantity + delta;
      if (newQty >= 1 && newQty <= 99) {
        this.quantity = newQty;
        if (this.qtyInput) {
          this.qtyInput.value = this.quantity;
        }
        this.updatePriceDisplay();
      }
    }

    /**
     * Update price display in the add to cart button
     */
    updatePriceDisplay() {
      if (!this.selectedVariant) {
        // Show first variant price if none selected
        const firstVariant = this.data.variants[0];
        if (firstVariant && this.addToCartPrice) {
          const totalPrice = firstVariant.price * this.quantity;
          this.addToCartPrice.textContent = this.formatMoney(totalPrice);
        }
        return;
      }

      const totalPrice = this.selectedVariant.price * this.quantity;

      if (this.addToCartPrice) {
        this.addToCartPrice.textContent = this.formatMoney(totalPrice);
      }
    }

    /**
     * Format price as money string
     */
    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const amountWithComma = amount.replace('.', ',');
      const amountNoDecimals = Math.round(cents / 100).toString();
      const amountNoDecimalsWithComma = amountNoDecimals;
      
      // Use shop money format if available, otherwise default to Euro
      if (this.data.moneyFormat) {
        let formatted = this.data.moneyFormat;
        // Replace all common Shopify money format placeholders
        formatted = formatted.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma);
        formatted = formatted.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimalsWithComma);
        formatted = formatted.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals);
        formatted = formatted.replace(/\{\{\s*amount\s*\}\}/g, amount);
        return formatted;
      }
      return '€' + amountWithComma;
    }

    /**
     * Validate that all required selections are made
     */
    validateSelections() {
      // For products without variants, always valid
      if (!this.data.hasVariants) {
        this.setAddToCartEnabled(this.data.variants[0]?.available !== false);
        return true;
      }

      const isValid = this.selectedVariant !== null;
      const isAvailable = isValid && this.selectedVariant.available;

      // Update button state
      this.setAddToCartEnabled(isAvailable);
      
      // Update button text based on availability
      if (this.addToCartText) {
        if (isValid && !isAvailable) {
          this.addToCartText.textContent = this.data.translations.out_of_stock || 'Sold out';
        } else {
          this.addToCartText.textContent = this.data.translations.add_to_cart || 'Add to Cart';
        }
      }
      
      // Show/hide validation message
      if (this.validationMessage) {
        this.validationMessage.style.display = isValid ? 'none' : 'block';
      }

      return isValid && isAvailable;
    }

    /**
     * Enable/disable add to cart button
     */
    setAddToCartEnabled(enabled) {
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = !enabled;
      }
    }

    /**
     * Handle add to cart
     */
    async handleAddToCart() {
      if (!this.validateSelections()) {
        return;
      }

      const translations = this.data.translations;
      
      // Set loading state
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.classList.add('loading');
      if (this.addToCartText) {
        this.addToCartText.textContent = translations.adding || 'Adding...';
      }

      try {
        // addToCart handles opening the cart drawer internally
        await this.addToCart();

        // Success
        if (this.addToCartText) {
          this.addToCartText.textContent = translations.added || 'Added!';
        }
        
        setTimeout(() => {
          if (this.addToCartText) {
            this.addToCartText.textContent = translations.add_to_cart || 'Add to Cart';
          }
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.classList.remove('loading');
        }, 2000);

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:update'));

      } catch (error) {
        console.error('Failed to add to cart:', error);
        this.showError(error.message || 'Failed to add to cart. Please try again.');
        if (this.addToCartText) {
          this.addToCartText.textContent = translations.add_to_cart || 'Add to Cart';
        }
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.classList.remove('loading');
      }
    }

    /**
     * Add item to cart
     */
    async addToCart() {
      const variantId = this.selectedVariant ? this.selectedVariant.id : this.data.variants[0]?.id;
      
      if (!variantId) {
        throw new Error('No variant selected');
      }

      // Check for UpCart
      const isUpCartActive = !!(
        window.UpCart ||
        window.upcart ||
        window.Upcart ||
        document.querySelector('script[src*="upcart"]') ||
        document.querySelector('link[href*="upcart"]')
      );

      const cart = !isUpCartActive && (
        document.querySelector('cart-notification') || 
        document.querySelector('cart-drawer')
      );

      // Build FormData
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', this.quantity);

      // Build URL with sections for cart drawer update
      let url = '/cart/add';
      if (!isUpCartActive && cart && typeof cart.getSectionsToRender === 'function') {
        const sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        const sectionsUrl = window.location.pathname;
        url += `?sections=${encodeURIComponent(sections)}&sections_url=${encodeURIComponent(sectionsUrl)}`;
      }

      // Add to cart
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formData
      });

      // Retry without sections param if it fails
      if (!response.ok && response.status === 400 && url.includes('sections=')) {
        const retryResponse = await fetch('/cart/add', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
          },
          body: formData
        });

        if (!retryResponse.ok) {
          const errorData = await retryResponse.json().catch(() => ({}));
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        // For UpCart, dispatch event to open cart
        if (isUpCartActive) {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }

        return await retryResponse.json();
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const data = await response.json();

      // Update cart drawer if present - renderContents will open the drawer
      if (!isUpCartActive && cart && data.sections) {
        cart.renderContents(data);
        // Don't call open() again - renderContents handles it
        return data;
      }

      // If no sections data, manually open the cart
      if (!isUpCartActive && cart) {
        if (typeof cart.open === 'function') {
          cart.open();
        }
      } else if (isUpCartActive) {
        // For UpCart, dispatch event to open cart
        document.dispatchEvent(new CustomEvent('cart:open'));
      }

      return data;
    }

    /**
     * Show error message
     */
    showError(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorMessage.style.display = 'block';

        setTimeout(() => {
          this.errorMessage.style.display = 'none';
        }, 5000);
      }
    }
  }

  /**
   * Initialize a custom variant selector instance
   */
  window.CustomVariantSelector.init = function(containerId) {
    return new VariantSelector(containerId);
  };

})();

